// A deploy replaces the hashed chunks, so a tab left open across it fails the
// next lazy import with one of these messages (navigation spec, "A tab left
// open across a deploy reloads once onto the new build").
const CHUNK_ERROR_PATTERN =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS/i;

const RELOAD_AT_KEY = "chunk-reload-at";
const RELOAD_WINDOW_MS = 60_000;

export function isChunkLoadError(error: unknown): boolean {
  return error instanceof Error && CHUNK_ERROR_PATTERN.test(error.message);
}

interface ChunkReloadRecoveryOptions {
  reload?: () => void;
  now?: () => number;
  storage?: Pick<Storage, "getItem" | "setItem">;
}

let uninstall: (() => void) | undefined;

// Reloads the page once per RELOAD_WINDOW_MS when Vite's build reports a failed
// dynamic import. It never calls `preventDefault()`: the import must still
// reject with its original error so the error boundary can recognize it.
export function installChunkReloadRecovery(options: ChunkReloadRecoveryOptions = {}): () => void {
  if (uninstall) {
    return uninstall;
  }
  const { reload = () => window.location.reload(), now = Date.now } = options;
  const onPreloadError = (): void => {
    try {
      const storage = options.storage ?? window.sessionStorage;
      const at = now();
      const elapsed = at - Number.parseFloat(storage.getItem(RELOAD_AT_KEY) ?? "");
      if (elapsed >= 0 && elapsed < RELOAD_WINDOW_MS) {
        return;
      }
      storage.setItem(RELOAD_AT_KEY, String(at));
    } catch {
      // Without a readable and writable record a reload could loop: fail
      // closed and let the error reach the boundary.
      return;
    }
    // A later task, so the router can commit the URL of a failed lazy route
    // first; reloading inside the event would reload the page the person left.
    setTimeout(reload, 0);
  };
  window.addEventListener("vite:preloadError", onPreloadError);
  uninstall = () => {
    window.removeEventListener("vite:preloadError", onPreloadError);
    uninstall = undefined;
  };
  return uninstall;
}
